import { useState, useEffect } from 'react';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface DeviceInfo {
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
}

/**
 * Custom Hook für Device Detection basierend auf Viewport-Größe
 * 
 * Breakpoints:
 * - Mobile: ≤ 850px (Touch-Panning)
 * - Tablet: 851px - 1300px (Touch-Rotation wie Desktop)
 * - Desktop: > 1300px (Maus-Rotation)
 */
export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    // Initial detection on first render
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const height = typeof window !== 'undefined' ? window.innerHeight : 768;
    
    const deviceType: DeviceType = 
      width <= 850 ? 'mobile' : 
      width <= 1300 ? 'tablet' : 
      'desktop';
    
    return {
      deviceType,
      isMobile: deviceType === 'mobile',
      isTablet: deviceType === 'tablet',
      isDesktop: deviceType === 'desktop',
      screenWidth: width,
      screenHeight: height
    };
  });

  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      const deviceType: DeviceType = 
        width <= 850 ? 'mobile' : 
        width <= 1300 ? 'tablet' : 
        'desktop';
      
      setDeviceInfo({
        deviceType,
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop',
        screenWidth: width,
        screenHeight: height
      });
    };

    // Update on resize
    window.addEventListener('resize', updateDeviceInfo);
    
    // Update on orientation change (wichtig für Tablets)
    window.addEventListener('orientationchange', () => {
      // Delay to ensure dimensions are updated after rotation
      setTimeout(updateDeviceInfo, 150);
    });
    
    // Initial update
    updateDeviceInfo();

    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
};

/**
 * Utility functions für CSS Media Queries
 */
export const BREAKPOINTS = {
  mobile: 850,
  tablet: 1300
} as const;

export const mediaQueries = {
  mobile: `(max-width: ${BREAKPOINTS.mobile}px)`,
  tablet: `(min-width: ${BREAKPOINTS.mobile + 1}px) and (max-width: ${BREAKPOINTS.tablet}px)`,
  desktop: `(min-width: ${BREAKPOINTS.tablet + 1}px)`,
  tabletAndUp: `(min-width: ${BREAKPOINTS.mobile + 1}px)`,
  mobileAndTablet: `(max-width: ${BREAKPOINTS.tablet}px)`
} as const;
